import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import treatmentService from '../services/TreatmentService';
import { Card, Button, Input, Alert } from '../components/ui';

/**
 * TreatmentDetailPage component for viewing and managing a specific treatment
 * @returns {React.ReactElement} TreatmentDetailPage component
 */
const TreatmentDetailPage = () => {
  // Get treatment id from URL params
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  // State
  const [treatment, setTreatment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration: 60,
    price: 0,
    isActive: true
  });

  // Check permissions
  const canViewTreatment = hasPermission('treatments', 'read');
  const canEditTreatment = hasPermission('treatments', 'write');

  // Fetch treatment data
  useEffect(() => {
    if (!id || !canViewTreatment) {
      return;
    }

    const fetchTreatment = () => {
      setLoading(true);
      try {
        const treatmentData = treatmentService.getTreatmentById(id);
        if (!treatmentData) {
          setError('找不到调理项目');
          return;
        }
        setTreatment(treatmentData);
        setFormData({
          name: treatmentData.name || '',
          description: treatmentData.description || '',
          duration: treatmentData.duration || 60,
          price: treatmentData.price || 0,
          isActive: treatmentData.isActive !== false
        });
        setError(null);
      } catch (err) {
        console.error('Error fetching treatment:', err);
        setError('获取调理项目数据失败');
      } finally {
        setLoading(false);
      }
    };

    fetchTreatment();
  }, [id, canViewTreatment]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    try {
      // Format data for submission
      const updatedTreatment = {
        ...formData,
        duration: Number(formData.duration),
        price: Number(formData.price)
      };

      treatmentService.updateTreatment(id, updatedTreatment);

      // Refresh treatment data
      const refreshedTreatment = treatmentService.getTreatmentById(id);
      setTreatment(refreshedTreatment);

      // Exit edit mode
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating treatment:', err);
      alert('更新调理项目失败');
    }
  };

  // Handle delete
  const handleDelete = () => {
    if (window.confirm('确定要删除此调理项目吗？此操作无法撤销。')) {
      try {
        treatmentService.deleteTreatment(id);
        navigate('/treatments');
      } catch (err) {
        console.error('Error deleting treatment:', err);
        alert('删除调理项目失败');
      }
    }
  };

  // If there's an error finding the treatment
  if (error === '找不到调理项目') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <Card className="max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            调理项目未找到
          </h2>
          <p className="text-gray-600 mb-6">
            您尝试访问的调理项目不存在或已被删除
          </p>
          <Button as={Link} to="/treatments">
            返回调理项目列表
          </Button>
        </Card>
      </div>
    );
  }

  // If user doesn't have permission
  if (!canViewTreatment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <Card className="max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-2xl flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            权限不足
          </h2>
          <p className="text-gray-600 mb-6">
            您没有查看调理项目详情的权限
          </p>
          <Button as={Link} to="/">
            返回首页
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-blue-50/30 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Back Button and Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center">
            <Button
              variant="secondary"
              onClick={() => navigate('/treatments')}
              className="mr-4"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              返回列表
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">
              {loading ? '加载调理项目...' : '调理项目详情'}
            </h1>
          </div>
          {canEditTreatment && !isEditing && (
            <div className="mt-4 sm:mt-0 space-x-3">
              <Button onClick={() => setIsEditing(true)}>
                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                编辑项目
              </Button>
              <Button variant="danger" onClick={handleDelete}>
                <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                删除项目
              </Button>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && error !== '找不到调理项目' && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center my-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
          </div>
        ) : isEditing ? (
          /* Edit Form */
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                编辑调理项目
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                修改调理项目的详细信息
              </p>
            </div>
            <div className="border-t border-gray-200">
              <form onSubmit={handleSubmit} className="p-6">
                <div className="grid grid-cols-6 gap-6">
                  {/* Name */}
                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      项目名称 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>

                  {/* Duration */}
                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="duration" className="block text-sm font-medium text-gray-700">
                      时长 (分钟) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="duration"
                      id="duration"
                      value={formData.duration}
                      onChange={handleInputChange}
                      required
                      min="5"
                      max="240"
                      step="5"
                      className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>

                  {/* Price */}
                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                      价格 (元)
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">¥</span>
                      </div>
                      <input
                        type="number"
                        name="price"
                        id="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        min="0"
                        step="10"
                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  {/* Active Status */}
                  <div className="col-span-6 sm:col-span-3">
                    <div className="flex items-center h-full">
                      <div className="flex items-center">
                        <input
                          id="isActive"
                          name="isActive"
                          type="checkbox"
                          checked={formData.isActive}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                          项目开放预约
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="col-span-6">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                      项目描述
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      rows={4}
                      value={formData.description}
                      onChange={handleInputChange}
                      className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      placeholder="描述调理项目的目的、适应症、方法等"
                    ></textarea>
                  </div>
                </div>

                <div className="pt-5 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    保存
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : treatment ? (
          /* View Treatment Details */
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {treatment.name}
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  调理项目详细信息
                </p>
              </div>
              <div>
                {treatment.isActive ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    开放预约
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    已停用
                  </span>
                )}
              </div>
            </div>
            <div className="border-t border-gray-200">
              <dl>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">时长</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {treatment.duration} 分钟
                  </dd>
                </div>

                {treatment.price !== undefined && (
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">价格</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {treatment.price > 0 ? `¥${treatment.price}` : '免费'}
                    </dd>
                  </div>
                )}

                {treatment.description && (
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">描述</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 whitespace-pre-line">
                      {treatment.description}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default TreatmentDetailPage;